import { useState } from "react";
import { Search, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@packages/hooks/useAnalytics";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { TextLinear, Text } from "@core/text";
import {
  deleteLessonPlannerV2,
  getGetLessonPlannersV2QueryKey,
  useGetLessonPlannersV2,
  useUpdateLessonPlannerV2,
} from "@packages/api/madlenAPI";
import { GetLessonPlannersV2PlanType } from "@packages/api/madlenAPI.schemas";
import { PastLessonPlanCard } from "../PastLessonPlanCard";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData } from "@packages/constant/CourseData";

const NewPastDailyPlans = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const { trackEvent: track } = trackEvent();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data: lessonPlans, isLoading } = useGetLessonPlannersV2({
    planType: GetLessonPlannersV2PlanType.DAILY,
  });

  const { mutateAsync: updateLessonPlanner } = useUpdateLessonPlannerV2({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetLessonPlannersV2QueryKey({
            planType: GetLessonPlannersV2PlanType.DAILY,
          }),
        });
      },
      onError: () => {
        addToast({
          title: t("lessonPlanner.titleUpdateError"),
          description: t("lessonPlanner.titleUpdateErrorDescription"),
          variant: "error",
        });
      },
    },
  });

  const handleDelete = async (planId: number) => {
    track(
      {
        category: "ContentDelete",
        action: "daily.planner.past.plans.delete",
        label: "DailyPlannerPastPlans delete click",
        nonInteraction: false,
      },
      {
        location: "/daily-planner-past-plans/delete",
        element: "delete-button",
      },
    );
    await deleteLessonPlannerV2(planId);
    queryClient.invalidateQueries({
      queryKey: getGetLessonPlannersV2QueryKey({
        planType: GetLessonPlannersV2PlanType.DAILY,
      }),
    });
  };

  const handleViewDetails = (planId: number) => {
    track(
      {
        category: "ContentViewDetail",
        action: "daily.planner.past.plans.view.detail",
        label: "DailyPlannerPastPlans view details click",
        nonInteraction: false,
      },
      {
        location: "/daily-planner-past-plans/view-detail",
        element: "view-detail-button",
      },
    );
    navigate(`/lesson-planning-tools/daily-plan/${planId}`);
  };

  const handleClickGenerate = () => {
    track(
      {
        category: "ContentGeneration",
        action: "daily.planner.past.plans.generate",
        label: "DailyPlannerPastPlans generation click",
        nonInteraction: false,
      },
      {
        location: "/daily-planner-past-plans/generate",
        element: "generate-button",
      },
    );
    navigate("/lesson-planning-tools/daily-plan/new");
  };

  const handleClickFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    track(
      {
        category: "ContentFilter",
        action: "daily.planner.past.plans.filter",
        label: "DailyPlannerPastPlans filter click",
        nonInteraction: false,
      },
      {
        location: "/daily-planner-past-plans/filter",
        element: "filter-button",
      },
    );
    setSelectedCourse(e.target.value);
  };

  const courseTypes = Object.keys(CourseData);

  const filteredPlans = lessonPlans?.data?.filter((plan) => {
    const matchesSearch = searchTerm
      ? plan.name?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const matchesCourse = selectedCourse
      ? plan.courseType === selectedCourse
      : true;

    return matchesSearch && matchesCourse;
  });

  const showEmptyState =
    !isLoading && (!filteredPlans || filteredPlans.length === 0);

  const handleUpdateTitle = async (planId: number, newTitle: string) => {
    await updateLessonPlanner({
      id: planId,
      data: {
        name: newTitle,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
      <div className="container max-w-7xl mx-auto px-4 py-6">
        {/* Hero Section */}
        <div className="mb-8 text-center space-y-4">
          <div className="max-w-4xl mx-auto">
            <TextLinear
              variant={"heroTitle"}
              className="leading-tight"
              from="from-emerald-600 dark:from-emerald-400"
              to="to-green-600 dark:to-green-400"
            >
              dailyLessonPlan.title
            </TextLinear>
          </div>
          <Text
            variant="p"
            status="secondary"
            size="base"
            className="max-w-2xl mx-auto"
          >
            dailyLessonPlan.description
          </Text>
          <div className="pt-2">
            <button
              onClick={handleClickGenerate}
              className="btn bg-gradient-to-r from-emerald-600 to-green-600 hover:opacity-90 text-white px-8 py-3 text-base rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-1 font-medium"
            >
              {t("dailyLessonPlan.createNew")}
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        {/* <div className="mb-6 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-4 justify-between flex-col md:flex-row">
            <select
              value={selectedCourse}
              onChange={handleClickFilter}
              className="px-5 py-3.5 border rounded-xl text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 w-full md:max-w-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 cursor-pointer hover:border-emerald-300 dark:hover:border-emerald-600"
            >
              <option value="">
                {t("dailyLessonPlan.filters.allCourses")}
              </option>
              {courseTypes.map((courseType) => (
                <option key={courseType} value={courseType}>
                  {t(`courseType.${courseType}`)}
                </option>
              ))}
            </select>

            <div className="relative flex flex-1 w-full md:max-w-md">
              <input
                type="text"
                placeholder={t("dailyLessonPlan.search.placeholder")}
                className="pl-12 pr-4 py-3.5 border rounded-xl w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 hover:border-emerald-300 dark:hover:border-emerald-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                size={20}
                className="absolute left-4 top-4 text-gray-400 dark:text-gray-500"
              />
            </div>
          </div>
        </div> */}

        {/* Lesson Plans Grid */}
        {!showEmptyState && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {filteredPlans?.map((plan) => (
              <PastLessonPlanCard
                key={plan.id}
                plan={plan}
                onViewDetails={handleViewDetails}
                onDelete={() => setShowDeletePopup(plan.id)}
                colorTheme="default"
                onUpdateTitle={handleUpdateTitle}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {showEmptyState && (
          <div className="flex flex-col items-center justify-center min-h-[500px] space-y-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-sm p-16 border border-gray-100 dark:border-gray-700">
            <div className="p-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 ring-8 ring-emerald-50/50 dark:ring-emerald-900/20">
              <Package
                size={64}
                className="text-emerald-600 dark:text-emerald-400"
              />
            </div>
            <div className="text-center space-y-4 max-w-lg">
              <h3 className="text-3xl font-semibold text-gray-900 dark:text-white">
                {searchTerm || selectedCourse
                  ? t("dailyLessonPlan.noResults.title")
                  : t("dailyLessonPlan.empty.title")}
              </h3>
              <Text variant="p" status="subtle" size="lg">
                {searchTerm || selectedCourse
                  ? "dailyLessonPlan.noResults.description"
                  : "dailyLessonPlan.empty.description"}
              </Text>
            </div>
            <button
              onClick={handleClickGenerate}
              className="btn bg-gradient-to-r from-emerald-600 to-green-600 hover:opacity-90 text-white px-10 py-5 text-lg rounded-2xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-1 font-medium"
            >
              {t("dailyLessonPlan.empty.createButton")}
            </button>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("dailyPlanner.confirmDelete", {
              title: filteredPlans?.find((plan) => plan.id === showDeletePopup)
                ?.name,
            })}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={() => handleDelete(showDeletePopup)}
          />
        )}
      </div>
    </div>
  );
};

export default NewPastDailyPlans;
